<template>
  <div>
    <GroupOrderHeaderBand
      :sectionDescription="
        'Please fill in your shipping address below. Your contact information will only be used by our carriers for delivery.'
      "
      :showFixedHeader="true"
    />

    <section class="section-holder">
      <div>
        <div class="arrow-link margin" @click="$router.go(-1)">
          <router-link to>
            <span class="inter">Back</span>
          </router-link>
        </div>
      </div>

      <div class="group-order-container">
        <h6 class="ship-header text-center">Shipping address</h6>
        <form @submit.prevent>
          <section class="form-holder">
            <div class="form-group relative">
              <input
                readonly
                type="text"
                id="email"
                class="form-control"
                placeholder="Work email"
                v-model="recipient.email"
                :class="{ input__error: $v.recipient.email.$error }"
                @blur="$v.recipient.email.$touch()"
              />
              <span
                class="error-message-info"
                v-if="$v.recipient.email.$error"
                >{{
                  !$v.recipient.email.email
                    ? 'Please enter a valid email.'
                    : 'Email is required.'
                }}</span
              >
            </div>

            <div class="form-group relative">
              <input
                autocomplete="off"
                type="text"
                id="name"
                class="form-control"
                placeholder="Full name"
                v-model="recipient.name"
                :class="{ input__error: $v.recipient.name.$error }"
                @blur="$v.recipient.name.$touch()"
              />
              <span class="error-message-info" v-if="$v.recipient.name.$error"
                >{{
                  !$v.recipient.name.isValidRegex
                    ? 'Only latin characters are allowed.'
                    : 'Name is required.'
                }}
              </span>
            </div>

            <div class="form-group relative">
              <input
                autocomplete="off"
                type="text"
                id="company"
                class="form-control"
                placeholder="Company"
                v-model="recipient.company"
                :class="{ input__error: $v.recipient.company.$error }"
                @blur="$v.recipient.company.$touch()"
              />
              <span
                class="error-message-info"
                v-if="$v.recipient.company.$error"
                >{{
                  !$v.recipient.company.isValidRegex
                    ? 'Only latin characters are allowed.'
                    : 'Company name is required.'
                }}
              </span>
            </div>

            <div
              class="phone-group relative"
              :class="{ input__error: $v.recipient.phone.$error }"
            >
              <vue-tel-input
                id="phone"
                class="form-control"
                :input-options="{ placeholder: 'Phone' }"
                defaultCountry="US"
                :autoDefaultCountry="false"
                v-model="recipient.phone"
                :class="{ input__error: $v.recipient.phone.$error }"
              />
              <span class="error-message-info" v-if="$v.recipient.phone.$error"
                >Phone is required.</span
              >
            </div>

            <div class="form-group country">
              <country-select
                className="custom"
                v-model="recipient.country"
                :country="recipient.country"
                :placeholder="'Select a shipping country'"
                topCountry="US"
                :class="{ input__error: $v.recipient.country.$error }"
                @blur="$v.recipient.country.$touch()"
              />
            </div>

            <div class="form-group relative">
              <input
                type="text"
                id="address_one"
                class="form-control"
                name="address_one"
                placeholder="Address 1"
                @input.once="handleAddressInput()"
                v-model="recipient.addressOne"
                :class="{ input__error: $v.recipient.addressOne.$error }"
                @blur="$v.recipient.addressOne.$touch()"
              />
              <span
                class="error-message-info"
                v-if="$v.recipient.addressOne.$error"
                >{{
                  !$v.recipient.addressOne.isValidRegex
                    ? 'Only latin characters are allowed.'
                    : 'Address is required.'
                }}</span
              >
            </div>

            <div class="form-group relative">
              <input
                autocomplete="off"
                type="text"
                id="address_two"
                class="form-control"
                placeholder="Address 2"
                @input="checkAddressLimit('recipient', 'addressTwo')"
                v-model="recipient.addressTwo"
                :class="{ input__error: $v.recipient.addressTwo.$error }"
                @blur="$v.recipient.addressTwo.$touch()"
              />
              <span
                class="error-message-info"
                v-if="$v.recipient.addressTwo.$error"
                >Only latin characters are allowed.</span
              >
            </div>

            <div class="form-group inline-inputs">
              <div class="relative">
                <input
                  type="text"
                  id="city"
                  class="form-control first"
                  placeholder="City"
                  v-model="recipient.city"
                  :class="{ input__error: $v.recipient.city.$error }"
                  @blur="$v.recipient.city.$touch()"
                />
                <span
                  class="error-message-info bottom"
                  v-if="$v.recipient.city.$error"
                  >{{
                    !$v.recipient.city.isValidRegex
                      ? 'Only latin characters are allowed.'
                      : 'City is required.'
                  }}</span
                >
              </div>

              <div
                class="form-control region relative"
                :class="{ input__error: $v.recipient.state.$error }"
              >
                <region-select
                  :isCustom="true"
                  className="custom-state"
                  v-model="recipient.state"
                  :country="recipient.country"
                  :region="recipient.state"
                  :class="{ input__error: $v.recipient.state.$error }"
                  @blur="$v.recipient.state.$touch()"
                />
                <span
                  class="error-message-info bottom"
                  v-if="$v.recipient.state.$error"
                  >Region is required.</span
                >
              </div>

              <div class="relative">
                <input
                  type="text"
                  id="zipcode"
                  class="form-control second"
                  placeholder="Zip"
                  v-model="recipient.zipcode"
                  :class="{ input__error: $v.recipient.zipcode.$error }"
                  @blur="$v.recipient.zipcode.$touch()"
                />
                <span
                  class="error-message-info bottom"
                  v-if="$v.recipient.zipcode.$error"
                  >{{
                    !$v.recipient.zipcode.isValidRegex
                      ? 'Only latin characters are allowed.'
                      : 'Zipcode is required.'
                  }}</span
                >
              </div>
            </div>

            <div
              class="form-group extra-field"
              v-for="(field, index) in recipient.customFields"
              :key="index"
            >
              <input
                autocomplete="off"
                :placeholder="field.name"
                type="text"
                class="form-control"
                v-model="field.value"
              />
            </div>
          </section>
        </form>

        <div class="flex justify-center">
          <button
            class="button-gemnote button-gemnote--small"
            :class="{ 'button-gemnote--disabled': updatingRecipientInfo }"
            :disabled="updatingRecipientInfo"
            @click="handleSubmit"
          >
            <span>Next</span>
          </button>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import { email, required, helpers } from 'vuelidate/lib/validators'
import { mapState } from 'vuex'
import GroupOrderHeaderBand from './GroupOrderHeaderBand'
let autocomplete
import AddressLimitMixin from '../../mixins/AddressLimit'
const recaptchaScript = document.createElement('script')
recaptchaScript.setAttribute(
  'src',
  `https://maps.googleapis.com/maps/api/js?libraries=places&key=${process.env.VUE_APP_MAPS_API_KEY}`
)
document.head.appendChild(recaptchaScript)
export default {
  name: 'GroupOrderRecipientForm',
  mixins: [AddressLimitMixin],
  components: {
    GroupOrderHeaderBand
  },
  computed: {
    ...mapState({
      recipient: state => state.groupOrder.recipient,
      updatingRecipientInfo: state => state.groupOrder.updatingRecipientInfo
    })
  },

  validations: {
    recipient: {
      name: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      },
      email: { required, email },
      company: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      },
      phone: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^\+?(\d{1,3})?[-.\s]?(\(?\d{1,4}?\)?[-.\s]?)?[\d-.\s]{3,15}$/
        )
      },
      country: { required },
      addressOne: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      },
      addressTwo: {
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      },
      city: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      },
      state: { required },
      zipcode: {
        required,
        isValidRegex: helpers.regex(
          'matches',
          /^[A-Za-z0-9äöüÄÖÜß .,?!;:'(){}[\]/\\&%@#_$*+=<>^-]+$/
        )
      }
    }
  },
  watch: {
    'recipient.addressOne'() {
      this.checkAddressLimit('recipient', 'addressOne')
    }
  },
  methods: {
    handleAddressInput() {
      this.handleSearchField('address_one')
    },
    handleSearchField(selector) {
      const google = window.google
      const input = document.querySelector(`[name = ${selector}]`)
      if (input) {
        autocomplete = new google.maps.places.Autocomplete(input, {
          fields: ['formatted_address', 'address_components'],
          types: ['address']
        })

        autocomplete.addListener('place_changed', this.fillInAddress)
      }
    },

    fillInAddress() {
      if (autocomplete) {
        const place = autocomplete.getPlace()
        if (place.address_components) {
          this.recipient.addressOne = ''
          for (const component of place.address_components) {
            const componentType = component.types[0]

            switch (componentType) {
              case 'street_number': {
                this.recipient.addressOne += `${component.long_name} `
                break
              }
              case 'route': {
                this.recipient.addressOne += component.long_name
                break
              }

              case 'country': {
                this.recipient.country = component.short_name
                break
              }

              case 'administrative_area_level_1': {
                this.recipient.state = component.short_name
                break
              }

              case 'locality': {
                this.recipient.city = component.long_name
                break
              }

              case 'postal_code': {
                this.recipient.zipcode = component.long_name
                break
              }

              default: {
                break
              }
            }
          }
        }
      }
    },
    handleSubmit() {
      this.$v.recipient.$touch()
      if (this.$v.recipient.$error === true) {
        return
      }
      let errors = []

      if (!this.recipient.phone) {
        errors.push('Phone number is required.')
      }

      if (errors.length) {
        this.$store.dispatch('notification/pushNotification', {
          type: 'error',
          messages: errors
        })
        return
      }

      this.$store.dispatch('groupOrder/updateRecipientInfo')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/global.scss';

.arrow-link {
  margin-top: 2rem;
}

.button-gemnote {
  margin-top: 3rem;
  width: auto;
  height: 2.5rem;
  padding: 0 2rem;
}

.section-holder {
  width: 80%;
  margin: 2rem auto;
  display: flex;
  position: relative;
  top: 6rem;
}

.ship-header {
  margin-top: 20px !important;
  display: flex;
  font-size: 1rem;
  margin: 40px 0;
  -webkit-box-pack: justify;
  -ms-flex-pack: justify;
  justify-content: center;
  align-items: center;
  font-weight: 700;
}

form {
  width: 75%;
  margin: 0 auto;

  .form-holder {
    border: 1px solid #24292c;
  }

  input {
    height: auto !important;
    padding: 1rem 1.25rem !important;

    &.input__error {
      border-bottom: 1px solid $gem-terraorange !important;
    }

    &::placeholder {
      text-transform: uppercase;
      font-size: 0.813rem;
      color: #9b9b9b;
    }
  }

  .form-group {
    margin-bottom: 0rem !important;

    &.extra-field {
      border-top: 1px solid #24292c;

      input {
        border-bottom: none;
        &.is-last {
          border-bottom: 1px solid #24292c;
        }
      }
    }

    &.country {
      border-bottom: 1px solid #24292c;
      padding: 1rem 1.25rem;
    }
  }

  .phone-group {
    margin-bottom: 0rem !important;
    border-bottom: 1px solid #24292c;
    padding: 0.5rem 1.25rem;

    &.input__error {
      border-bottom: 1px solid $gem-terraorange !important;
    }

    .vue-tel-input {
      border-bottom: none !important;

      &:active,
      :focus {
        border-bottom: none !important;
      }
    }
  }

  select {
    background: transparent;
  }
}

.inline-inputs {
  display: flex;
  /* gap: 1rem; */

  input {
    border-bottom: none !important;
  }
}

.form-control {
  &.region {
    border-right: 1px solid #24292c !important;
    border-left: 1px solid #24292c !important;
    padding: 0 12px;
    &.input__error {
      border-bottom: 1px solid $gem-terraorange !important;
    }
  }
}

.error-message-info {
  position: absolute;
  bottom: 0;
  right: 1%;
  font-size: 10px;
  color: #da5737;

  &.bottom {
    top: 100%;
    left: 0;
    text-align: center;
  }
}

@media (max-width: 1150px) {
  .arrow-link {
    position: inherit;
    left: 0rem;
    top: 0rem;
  }

  .group-order-container {
    width: 100% !important;
  }
}

@media (max-width: 575.98px) {
  .group-order-container {
    width: 100%;
    padding: 1rem 1.5rem 5rem 1.5rem;
    margin: 2rem auto;
  }

  .section-holder {
    width: 100%;
    flex-wrap: wrap;
    margin: 2rem auto;
  }

  .arrow-link {
    margin-left: 1rem;
  }
}

@media (min-width: 576px) and (max-width: 767.98px) {
  .group-order-container {
    width: 100%;
    padding: 1rem 1.5rem 5rem 1.5rem;
    margin: 2rem auto;
  }

  .section-holder {
    width: 100%;
    flex-wrap: wrap;
    margin: 2rem auto;
  }

  .arrow-link {
    margin-left: 1rem;
  }
}

@media (max-width: 860px) {
  .inline-inputs {
    flex-direction: column;
  }
  .form-control.region {
    border-right: none !important;
    border-left: none !important;
    border-top: 1px solid #24292c !important;
    border-bottom: 1px solid #24292c !important;
  }

  .error-message-info {
    bottom: 0;
    right: 1%;
    &.bottom {
      top: 70%;
      left: 50%;
      text-align: right;
    }
  }
}
</style>
